import React from 'react';
import $ from 'legacy/jquery';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Modal from 'legacy/modal';
import _ from 'underscore';

import styled from 'styled-components';

// There are global styles that turn all links inside jQuery UI widgets
// a certain blue color. Unfortunately `<Button />` ends up rendering to a
// `<a role="button"></a>` which accidentally gets a blue color as well.
//
// Make sure it this doesn't happen.
const ModalContainer = styled.div`
  &.ui-widget-content a[role='button'] {
    color: inherit;
  }
`;

/*
 This component wraps our jQueryUI Modal non-react library.
 It decides to open or close the modal based on the `isOpen` prop, and provides a callback of `onRequestClose`
 for when the user closes the modal through a jQueryUI handler (such as hitting the escape key).

 The contents of the modal should be passed as a child of this component.

 `autofocus` can be set to false to disable the default focus of a selectable element (see #autoFocusDummyTarget)
 */
export default class ControlledModal extends React.Component {
  static propTypes = {
    // Either children or render is required
    children: PropTypes.node,
    render: PropTypes.func,

    isOpen: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onRequestOpen: PropTypes.func,

    autoResize: PropTypes.bool,
    autofocus: PropTypes.bool,
    preventCloseOnOutsideClick: PropTypes.bool,
    closeOnEscape: PropTypes.bool,
    id: PropTypes.string,
    scrollable: PropTypes.bool,
    title: PropTypes.string,
    width: PropTypes.oneOfType([
      PropTypes.string, // e.g. 'auto'
      PropTypes.number,
    ]),
    height: PropTypes.oneOfType([
      PropTypes.string, // e.g. 'auto'
      PropTypes.number,
    ]),
    providesFor: PropTypes.string,
  };

  static defaultProps = {
    autofocus: true,
    title: '',
    width: 800,
    draggable: true,
    autoResize: false,
    closeOnEscape: true,
    preventCloseOnOutsideClick: false,
  };

  state = {
    readyToRenderChildren: false,
  };

  render() {
    let { title, id, scrollable, providesFor } = this.props;

    /*
      This outer <div> is necessary because jQuery UI manipulates the ModalContainer DOM node directly.
      When you open a modal, ModalContainer ends up appended to a different part of the DOM. However, React
      does not know that the ModalContainer DOM node has moved. Upon re-rendering it will try to insert things
      before or after the ModalContainer DOM node and blow up.
    */
    return createPortal(
      <div style={{ display: 'inline' }}>
        <ModalContainer
          ref={(modal) => (this.modalElem = modal)}
          className="modal"
          id={id}
          data-modal-title={title}
          data-provides="controlled_modal"
          data-provides-for={providesFor}
        >
          {this.autoFocusDummyTarget()}
          <div className={classNames('modal-body', { scrollable })}>
            {this.shouldRenderChildren() && this.renderChildren()}
          </div>
        </ModalContainer>
      </div>,
      document.body
    );
  }

  shouldRenderChildren = () => {
    // We wait for the jquery-ui modal to open before rendering children components because
    // they may rely on the dom changes.
    return this.props.isOpen && this.state.readyToRenderChildren;
  };

  renderChildren() {
    if (this.props.render && typeof this.props.render === 'function') {
      return this.props.render({
        onModalContentHeightChange: this.adjustHeight,
        onModalTitleChange: this.updateTitle,
      });
    } else {
      return this.props.children;
    }
  }

  UNSAFE_componentWillReceiveProps({ title }) {
    if (this.props.title !== title) {
      this.updateTitle(title);
    }
  }

  cancelOnEscape = (event) => {
    if (event.keyCode === 27) {
      this.close();
    }
  };

  onWindowResize = _.debounce(() => {
    this.adjustHeight();
  }, 100);

  componentWillUnmount() {
    if (this.props.closeOnEscape) {
      document.removeEventListener('keydown', this.cancelOnEscape);
    }

    if (this.props.autoResize) {
      window.removeEventListener('resize', this.onWindowResize);
    }
  }

  componentDidMount() {
    if (this.props.closeOnEscape) {
      document.addEventListener('keydown', this.cancelOnEscape);
    }

    if (this.props.autoResize) {
      window.addEventListener('resize', this.onWindowResize);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen) {
      if (this.props.isOpen === true) {
        if (
          this.props.onRequestOpen &&
          typeof this.props.onRequestOpen === 'function'
        ) {
          this.props.onRequestOpen();
        }
        this.open();
      } else {
        this.close();
      }
    }
  }

  open = () => {
    const config = {
      beforeClose: this.props.onRequestClose,
      preventCloseOnOutsideClick: this.props.preventCloseOnOutsideClick,
      config: {
        width: this.props.width,
        height: this.props.height,
        draggable: this.props.draggable,
        closeOnEscape: this.props.closeOnEscape,
        open: function (event, ui) {
          $('body').css({ overflow: 'hidden' });
        },
        beforeClose: function () {},
        close: function (event, ui) {
          var otherModalsOpen = $('.ui-widget-overlay').length > 0;
          if (!otherModalsOpen) {
            $('body').css({ overflow: 'inherit' });
          }
        },
      },
    };

    if (!this.modal) {
      this.modal = new Modal(this.modalElem, config);
      this.modal.open();
      this.setState({ readyToRenderChildren: true }, this.adjustHeight);
    }

    this.adjustHeight();
  };

  close = () => {
    if (this.modal) {
      const cancelClose = this.modal.close();
      if (cancelClose === false) {
        return;
      }
      this.modal = undefined;
      this.setState({ readyToRenderChildren: false });
    }
  };

  adjustHeight = () => {
    if (this.props.isOpen) {
      this.modal.adjustHeight();
    }
  };

  updateTitle = (title) => {
    if (this.props.isOpen) {
      this.modal.setTitle(title || this.props.title);
    }
  };

  autoFocusDummyTarget() {
    /*
    1) The jQueryUI Modal automatically focuses the first element with [autofocus] or the first tabbable element. It has
    no way to disable this, so we offer an option to place a "dummy" element that will be focused instead.
    See https://api.jqueryui.com/dialog
    2) React does not allow for invalid attributes to be set on dom elements, and the modal searches for
    autofocus instead of autoFocus, so we have to live dangerously.
    */
    if (this.props.autofocus) {
      return null;
    } else {
      return <span dangerouslySetInnerHTML={{ __html: '<span autofocus/>' }} />;
    }
  }
}
